import t from 'react-translate';
import uuid from 'react-uuid';
import {
  Fragment,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import * as humps from 'humps';
import { isEmpty } from 'lodash';
import { css } from '@emotion/react';

// redux
import { getCurrentCourse, getFlatCourseAliases } from 'redux/selectors/course';
import { initiateAIRolePlayScenarioGeneration, validateLectureContentForAiRolePlay } from 'redux/actions/ai-role-play';
import { getCurrentUser } from 'redux/selectors/users';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { wrapThunkAction } from 'redux/utils';

import NvTextArea from 'shared/components/inputs/nv-text-area';
import PusherService from 'shared/services/pusher-service';
import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import { ModalWorkflowContext } from 'lecture_pages/components/workflows/modal-workflow';
import NvIcon from 'shared/components/nv-icon';
import GradientAiIcon from 'lecture_pages/components/left-panel/novo-ai/components/shared/gradient-ai-icon';
import NvGradientBorderButton from 'shared/components/button/nv-gradient-border-button';
import { danger } from 'styles/global_defaults/colors';
import NvPopover from 'shared/components/nv-popover';
import ClickableContainer from 'components/clickable-container';
import { config } from '../../../../../config/pendo.config.json';
import { AIConfigStep, LectureContentQualityStatus } from '.';
import AiGenerationPreview from './ai-scenario-generating-preview';

type SetUpBasicsProps = {
  setStep: (step: AIConfigStep) => void;
  onCancel: () => void;
  setLectureContentQualityStatus: (status: LectureContentQualityStatus) => void;
};

enum AiScenarioGenerationStatus {
  IDLE = 'idle',
  INITIATED = 'initiated',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

const AI_ROLE_PLAY_SCENARIO_CREATION_EVENT = 'ai_role_play_creation_completed';
const AI_ROLE_PLAY_SCENARIO_CREATION_FAILED_EVENT = 'ai_role_play_creation_failed';

const instructionPopoverStyles = css`
  min-width: 400px !important;
`;

const getPopoverContent = () => (
  <div className='p-4'>
    <p className='text-regular font-weight-bolder mb-0'>
      {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.LEARN_ABOUT_APPROACH_POPOVER.TITLE()}
    </p>
    <p className='text-regular mb-0 mt-2'>
      {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.LEARN_ABOUT_APPROACH_POPOVER.FIRST_DESCRIPTION()}
    </p>
    <p className='text-regular mb-0 mt-4'>
      {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.LEARN_ABOUT_APPROACH_POPOVER.SECOND_DESCRIPTION()}
    </p>
    <p className='text-regular mb-0 mt-4'>
      {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.LEARN_ABOUT_APPROACH_POPOVER.THIRD_DESCRIPTION()}
    </p>
  </div>
);

const SetUpBasics = (props: SetUpBasicsProps) => {
  const {
    setStep,
    onCancel,
    setLectureContentQualityStatus,
  } = props;

  const dispatch = useDispatch();

  const [showInstructionsPopover, setShowInstructionsPopover] = useState(false);
  const params = useLecturePageParams();
  const { id: courseId } = useSelector((state) => getCurrentCourse(state));
  const courseAliases = useSelector(state => getFlatCourseAliases(state, params.catalogId));
  const { anonymizedIdentifier } = useSelector(getCurrentUser);
  const { getValues, reset } = useFormContext();
  const { settings, mode } = useContext(ModalWorkflowContext);
  const [aiScenarioGenerationStatus, setAiScenarioGenerationStatus] = useState(AiScenarioGenerationStatus.IDLE);
  const [isValidatingLectureContent, setIsValidatingLectureContent] = useState(false);

  const generationTexts = useMemo(() => [
    t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.READING_CONTENT(),
    t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.WRITING_CONTEXT(),
    t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.CREATING_AI_OBJECTIONS(),
    t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.CREATING_LEARNER_GOALS(courseAliases),
    t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.FINAL_TOUCH(),
  ], [courseAliases]);

  const isAiGenerationDisabled = !getValues('contextDescription')
    || aiScenarioGenerationStatus === AiScenarioGenerationStatus.INITIATED
    || aiScenarioGenerationStatus === AiScenarioGenerationStatus.PROCESSING
    || isValidatingLectureContent;
  const isManualCreationDisabled = aiScenarioGenerationStatus === AiScenarioGenerationStatus.INITIATED
    || isValidatingLectureContent;

  const handleNext = useCallback(() => {
    setIsValidatingLectureContent(true);
    wrapThunkAction<{ contentQuality: LectureContentQualityStatus }>(
      dispatch(validateLectureContentForAiRolePlay({
        catalogId: params.catalogId,
        lecturePageId: params.lecturePageId,
      })),
    ).then((res) => {
      setLectureContentQualityStatus(res.payload.contentQuality);
    }).catch(() => {
      // Don't block advancing on transient validation failures — fall back to NOT_EVALUATED
      // so manual creation (and the post-AI-failure fallback) is still possible.
      setLectureContentQualityStatus(LectureContentQualityStatus.NOT_EVALUATED);
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SOMETHING_WENT_WRONG(),
        customColor: danger,
      }));
    }).finally(() => {
      setStep(AIConfigStep.SET_UP_INSTRUCTIONS);
      reset();
      setIsValidatingLectureContent(false);
    });
  }, [
    setStep,
    reset,
    dispatch,
    params.catalogId,
    params.lecturePageId,
    setLectureContentQualityStatus,
  ]);

  const handleAiGeneratedScenarioInjection = useCallback((generatedContent) => {
    const {
      title,
      description,
      context,
      learnerRole,
      aiCharacterName,
      aiCharacterRole,
      aiCharacterObjections,
      aiCharacterPersonality,
      learnerGoals,
      contentQuality,
      interactionMode,
      voiceOptionKey,
      aiCharacterFace,
    } = generatedContent;

    setLectureContentQualityStatus(contentQuality);
    reset({
      ...getValues(),
      title,
      description,
      context,
      aiCharacterName: aiCharacterName || t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.DEFAULT_AI_CHARACTER_NAME(),
      learnerRole,
      aiCharacterRole,
      aiCharacterPersonality,
      aiCharacterObjections: aiCharacterObjections.map((objection) => ({ id: uuid(), text: objection })),
      requiredAiLearnerGoal: { id: uuid(), text: learnerGoals[0] },
      aiLearnerGoals: learnerGoals.slice(1).map((goal) => ({ id: uuid(), text: goal })),
      interactionMode,
      voiceOptionKey,
      ...(!isEmpty(aiCharacterFace) && { aiCharacterFace }),
    }, { keepDefaultValues: true });
    setAiScenarioGenerationStatus(AiScenarioGenerationStatus.COMPLETED);
    setStep(AIConfigStep.SET_UP_INSTRUCTIONS);
  }, [reset, getValues, setStep, setLectureContentQualityStatus]);

  useEffect(() => {
    const identifier = anonymizedIdentifier.substring(0, 10);
    const channel = PusherService.initializeUserCourseChannel(identifier, courseId);
    if (!channel) return () => {};

    const handleCreationCompleted = (pusherData: Record<string, unknown>) => {
      const camelizedData = humps.camelizeKeys(pusherData) as Record<string, unknown>;
      if (camelizedData.contentQuality === LectureContentQualityStatus.CRITICALLY_INSUFFICIENT) {
        setLectureContentQualityStatus(LectureContentQualityStatus.CRITICALLY_INSUFFICIENT);
        setStep(AIConfigStep.SET_UP_INSTRUCTIONS);
        reset();
      } else {
        handleAiGeneratedScenarioInjection(camelizedData);
      }
    };

    const handleCreationFailed = () => {
      setAiScenarioGenerationStatus(AiScenarioGenerationStatus.FAILED);
      dispatch(addAlertMessage({
        type: AlertMessageType.WARNING,
        header: t.FORM.OOPS(),
        message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.CANNOT_GENERATE_SCENARIO(),
        customColor: danger,
      }));
    };

    channel.bind(AI_ROLE_PLAY_SCENARIO_CREATION_EVENT, handleCreationCompleted);
    channel.bind(AI_ROLE_PLAY_SCENARIO_CREATION_FAILED_EVENT, handleCreationFailed);

    return () => {
      channel.unbind(AI_ROLE_PLAY_SCENARIO_CREATION_EVENT, handleCreationCompleted);
      channel.unbind(AI_ROLE_PLAY_SCENARIO_CREATION_FAILED_EVENT, handleCreationFailed);
    };
  }, [
    anonymizedIdentifier,
    courseId,
    dispatch,
    handleAiGeneratedScenarioInjection,
    reset,
    setAiScenarioGenerationStatus,
    setLectureContentQualityStatus,
    setStep,
  ]);

  const handleGenerateRolePlay = () => {
    setAiScenarioGenerationStatus(AiScenarioGenerationStatus.INITIATED);
    wrapThunkAction(
      dispatch(initiateAIRolePlayScenarioGeneration({
        contextDescription: getValues('contextDescription'),
        lecturePageId: params.lecturePageId,
        catalogId: params.catalogId,
      })),
    ).then(() => {
      setAiScenarioGenerationStatus(AiScenarioGenerationStatus.PROCESSING);
    }).catch(() => {
      setAiScenarioGenerationStatus(AiScenarioGenerationStatus.FAILED);
      dispatch(addAlertMessage({
        type: AlertMessageType.WARNING,
        header: t.FORM.OOPS(),
        message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.CANNOT_GENERATE_SCENARIO(),
        customColor: danger,
      }));
    });
  };

  const handleCloseModal = () => {
    setAiScenarioGenerationStatus(AiScenarioGenerationStatus.IDLE);
    onCancel();
    reset();
  };

  return (
    <div>
      <div className='d-flex w-100'>
        <GradientAiIcon icon='gen-ai' size='small' className='pr-1' />
        <p className='mb-0 font-weight-bolder page-title-xs'>
          {settings.title(mode)}
        </p>
        <button
          type='button'
          onClick={handleCloseModal}
          className='border-0 bg-white d-flex ml-auto cursor-pointer'
          aria-label={t.FORM.CLOSE()}
          data-qa={config.pendo.activities.aiRolePlay.configModal.close}
        >
          <NvIcon size='smallest' icon='close' />
        </button>
      </div>
      {aiScenarioGenerationStatus === AiScenarioGenerationStatus.PROCESSING ? (
        <AiGenerationPreview
          aiGenerationTexts={generationTexts}
          showTick
        />
      ) : (
        <Fragment>
          <p className='mb-0 mt-6 page-title text-center'>
            {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.LETS_CREATE_ROLE_PLAY()}
          </p>
          <div className='mt-4 mx-4'>
            <p className='text-large-body mb-2 lh-25 mb-0 font-weight-normal'>
              {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.GENERATE_YOUR_ROLE_PLAY_CONTENT(courseAliases)}
            </p>
            <div className='d-inline-flex'>
              <NvPopover
                enabled
                show={showInstructionsPopover}
                rootClose
                content={getPopoverContent()}
                placement='bottom'
                className='ml-1'
                overlayStyles={instructionPopoverStyles}
                onHide={() => setShowInstructionsPopover(false)}
              >
                <ClickableContainer
                  onClick={() => setShowInstructionsPopover(!showInstructionsPopover)}
                  className='d-flex'
                >
                  <NvIcon
                    className='text-primary d-flex align-items-center'
                    size='smallest'
                    icon='info'
                  />
                  <p className='ml-2 text-primary text-regular mb-0 font-weight-bold'>
                    {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.LEARN_MORE_ABOUT_APPROACH()}
                  </p>
                </ClickableContainer>
              </NvPopover>
            </div>
            <NvTextArea
              withForm
              name='contextDescription'
              className='situation-textarea mt-4'
              textareaClassName='min-h-lg lh-30 text-large-body'
              placeholder={t.FORM.TYPE_HERE()}
              showErrorIcon={false}
            />
            <div className='d-flex justify-content-center mt-6'>
              <NvGradientBorderButton
                onClick={handleGenerateRolePlay}
                className='align-items-center py-2 px-5'
                ariaLabel={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.GENERATE_ROLE_PLAY()}
                disabled={isAiGenerationDisabled}
                dataQa={config.pendo.activities.aiRolePlay.configModal.generateRolePlay}
              >
                {isAiGenerationDisabled
                  ? <NvIcon icon='gen-ai' size='small' className='pr-2 text-gray-4' />
                  : <GradientAiIcon icon='gen-ai' size='small' className='pr-2' />}
                <span className={`${isAiGenerationDisabled ? 'text-gray-4' : ''} text-regular bold`}>
                  {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.GENERATE_ROLE_PLAY()}
                </span>
              </NvGradientBorderButton>
            </div>
            <button
              type='button'
              className={`${isManualCreationDisabled ? 'text-gray-4' : 'text-primary'} bg-white border-0 d-flex mx-auto mt-3 cursor-pointer font-weight-bold text-center`}
              onClick={handleNext}
              disabled={isManualCreationDisabled}
              data-qa={config.pendo.activities.aiRolePlay.configModal.manuallyCreate}
            >
              {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.MANUALLY_CREATE()}
            </button>
          </div>
        </Fragment>
      )}
    </div>
  );
};

export default SetUpBasics;
